import cn from 'classnames';
import { isEmpty } from 'ramda';
import { FC, useCallback } from 'react';
import { useParams } from 'react-router';

import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import { scrollToElementByID, trackEvent } from '@/shared/lib';
import { Skeleton } from '@/shared/ui';

import { FMXNavigationMode, reportsModel } from '@/entities/reports';
import { toothModel } from '@/entities/tooth';

import { useFDAEnvironment } from '@/features/fdaRestrictions';
import { Report2DSettingsBar } from '@/features/report2DSettingsBar';
import {
  ToothChart,
  ToothChartItemClickHandler,
  toothChartModel,
} from '@/features/toothChart';

import styles from './Report2DToothChart.module.scss';

type Report2DToothChartProps = {
  className?: string;
};

export const Report2DToothChart: FC<Report2DToothChartProps> = (props) => {
  const { className } = props;
  const { reportID } = useParams();
  const dispatch = useAppDispatch();
  const { isMobile } = useMedia();

  const { toothItems, toothTypesSet } = useAppSelector(
    toothChartModel.selectors.selectToothChartItems(reportID as string),
  );
  const customMode = useAppSelector(
    toothModel.selectors.selectToothChartCustomMode,
  );

  const { isFDA, isFDAVersion2 } = useFDAEnvironment();

  const isToothChartReady = !isEmpty(toothTypesSet);

  const handleToothClick = useCallback<ToothChartItemClickHandler>(
    ({ id, active, ISONumber }) => {
      trackEvent({
        eventName: 'ReportPageToothFormulaButtonClicked',
        properties: {
          tooth_number: ISONumber,
        },
      });

      if (customMode) {
        dispatch(toothModel.actions.toggleLocalROITeethIDs([id]));
      } else if (active) {
        dispatch(reportsModel.actions.setFocusedMetaImageID(''));
        dispatch(
          reportsModel.actions.setNavigationMode(FMXNavigationMode.ToothChart),
        );

        // NOTE: It needs to properly scroll to the tooth card which appears in the DOM later than this function executes if it's just a sync call.
        setTimeout(() => scrollToElementByID(id, 'smooth', 'start'), 0);

        dispatch(reportsModel.actions.setActiveToothID(id));
      }
    },
    [customMode, dispatch],
  );

  return (
    <ToothChart.Root
      size={isMobile ? 'mobile' : 'small'}
      className={cn(styles.container, className)}
    >
      {!isMobile && (
        <ToothChart.Header hideTitle>
          <Report2DSettingsBar className={styles.reportSettingsBar} />
        </ToothChart.Header>
      )}
      {isToothChartReady ? (
        <>
          {!isMobile && (
            <ToothChart.Tags
              toothTypesSet={toothTypesSet}
              reportID={reportID as string}
              isFDAMode={isFDAVersion2}
            >
              {/* FDA: annotators must not customize the teeth selection */}
              {!isFDA && (
                <ToothChart.CustomModeToggle
                  buttonSize="tiny"
                  className={styles.customModeToggle}
                />
              )}
            </ToothChart.Tags>
          )}

          <ToothChart.Grid>
            {toothItems.map((toothItem) => (
              <ToothChart.MetaItem
                key={toothItem.id}
                toothItem={toothItem}
                handleToothClick={handleToothClick}
              />
            ))}
          </ToothChart.Grid>
        </>
      ) : (
        <>
          <Skeleton.Filter
            filtersQuantity={4}
            height={20}
            gap={4}
            borderRadius={10}
          />

          <Skeleton width={484} height={126} borderRadius={4} />
        </>
      )}
    </ToothChart.Root>
  );
};
